import useBodyScrollLock from '../hooks/useBodyScrollLock';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Se emite en window al confirmar la edad, para que otras pantallas (p. ej. la
// bienvenida del registro de modelo) esperen a que este modal se cierre.
export const AGE_VERIFIED_EVENT = 'age-verified';

// Interruptor del aviso de mayoría de edad. Apagado a propósito: el contenido
// de las modelos ya está detrás de un login. Con false el aviso no se muestra
// en ningún lado y la edad cuenta como verificada, así que lo que esperaba su
// confirmación (el alta de modelo en Register.jsx) sigue de largo. Para volver
// a usarlo basta ponerlo en true.
export const AGE_GATE_ENABLED = false;

export const isAgeVerified = () => {
  if (!AGE_GATE_ENABLED) return true;
  try { return !!localStorage.getItem('ageVerified'); } catch (e) { return true; }
};

// El aviso solo aparece donde se ve contenido de las modelos: feeds, packs y
// perfiles (también bajo /client, /model, etc.), más el registro, cuya
// bienvenida de modelo espera esta confirmación. La home con la pestaña
// Modelos, el login y el resto quedan sin aviso, para que los buscadores y
// las personas vean la portada de YourPack sin nada encima.
const GATED_PATH = /^\/(?:(?:model|user|client|admin|assistant)\/)?(?:feed|feedPublic|FeedPublic|packs|listPacks|FeedByUser\/\d+|profileModal\/\d+)\/?$/;
const REGISTER_PATH = /^\/(?:register|registermodel|registerclient|[^/]+\/register)\/?$/;
const BOT_UA = /googlebot|bingbot|duckduckbot|yandex|baiduspider|applebot/i;

// Las pestañas Packs y Feeds de la home (DiscoverTabs) muestran lo mismo que
// /packs y /feed sin cambiar de ruta: lo piden con este evento.
export const AGE_REQUIRED_EVENT = 'age-required';
export const requireAgeVerification = () => window.dispatchEvent(new Event(AGE_REQUIRED_EVENT));

const AgeVerificationModal = () => {
  const { pathname } = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [requested, setRequested] = useState(false);
  const [language, setLanguage] = useState('en');

  // Content in both languages
  const content = {
    en: {
      title: 'Age Verification',
      paragraphs: [
        'This website is intended for adults only. By accessing this site, you confirm that you are of legal age in your jurisdiction to view adult content.',
        'By clicking "I am 18 or older", you agree to our Terms of Service and Privacy Policy, and confirm that you meet the legal age requirements.',
        'If you are not of legal age, please leave the site immediately.'
      ],
      rejectButton: 'I am under 18',
      confirmButton: 'I am 18 or older'
    },
    es: {
      title: 'Verificación de Edad',
      paragraphs: [
        'Este sitio web está destinado únicamente para mayores de 18 años. Al acceder, confirmas que tienes la edad legal requerida en tu jurisdicción para ver contenido para adultos.',
        'Al hacer clic en "Soy mayor de edad", aceptas nuestros Términos de Servicio y Política de Privacidad, y confirmas que cumples con los requisitos de edad legal.',
        'Si no cumples con la edad requerida, por favor abandona el sitio inmediatamente.'
      ],
      rejectButton: 'No soy mayor de edad',
      confirmButton: 'Soy mayor de edad'
    }
  };

  useEffect(() => {
    // Check browser language
    const userLang = navigator.language || navigator.userLanguage;
    if (userLang.startsWith('es')) {
      setLanguage('es');
    }

    const onRequired = () => setRequested(true);
    window.addEventListener(AGE_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(AGE_REQUIRED_EVENT, onRequired);
  }, []);

  // El pedido de una pestaña de la home vale solo para esa página.
  useEffect(() => {
    setRequested(false);
  }, [pathname]);

  const gated = requested || GATED_PATH.test(pathname) || REGISTER_PATH.test(pathname);

  useEffect(() => {
    // Los buscadores no tienen localStorage: el aviso les taparía la página y
    // Google lo usaría como título y descripción del sitio. Solo a ellos se
    // les omite; las personas siguen viendo el aviso.
    if (!AGE_GATE_ENABLED || BOT_UA.test(navigator.userAgent || '')) return;
    setIsVisible(gated && !isAgeVerified());
  }, [gated]);

  useBodyScrollLock(isVisible);

  const handleConfirm = () => {
    localStorage.setItem('ageVerified', 'true');
    setIsVisible(false);
    window.dispatchEvent(new Event(AGE_VERIFIED_EVENT));
  };

  const handleReject = () => {
    window.location.href = 'https://www.google.com';
  };

  if (!isVisible) return null;

  const { title, paragraphs, rejectButton, confirmButton } = content[language];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4" translate="no" data-nosnippet>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 mx-4">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            {title}
          </h2>
          <div className="text-gray-700 dark:text-gray-300 mb-6 text-left">
            {paragraphs.map((paragraph, index) => (
              <p key={index} className="mb-4">
                {paragraph}
              </p>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleReject}
              className="px-6 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 text-gray-800 font-medium rounded-[10px] transition-colors duration-200"
            >
              {rejectButton}
            </button>
            <button
              onClick={handleConfirm}
              className="px-6 py-2 bg-rose-500 hover:bg-rose-600 text-white font-medium rounded-full transition-colors duration-200"
            >
              {confirmButton}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgeVerificationModal;
